/**
 * scannerService.js
 * Pure canvas-based image processing pipeline.
 * Modular — swap any function independently.
 */

// ─── Homography (3×3) from 4 point correspondences ───────────────────────────
export function computeHomography(src, dst) {
  const A = [];
  for (let i = 0; i < 4; i++) {
    const [sx, sy] = src[i];
    const [dx, dy] = dst[i];
    A.push([-sx, -sy, -1, 0, 0, 0, dx * sx, dx * sy, dx]);
    A.push([0, 0, 0, -sx, -sy, -1, dy * sx, dy * sy, dy]);
  }
  const n = 8;
  for (let col = 0; col < n; col++) {
    let maxRow = col;
    for (let row = col + 1; row < n; row++) {
      if (Math.abs(A[row][col]) > Math.abs(A[maxRow][col])) maxRow = row;
    }
    [A[col], A[maxRow]] = [A[maxRow], A[col]];
    for (let row = col + 1; row < n; row++) {
      const factor = A[row][col] / A[col][col];
      for (let c = col; c <= n; c++) A[row][c] -= factor * A[col][c];
    }
  }
  const h = new Array(9).fill(0);
  for (let i = n - 1; i >= 0; i--) {
    h[i] = A[i][n] / A[i][i];
    for (let j = i - 1; j >= 0; j--) A[j][n] -= A[j][i] * h[i];
  }
  h[8] = 1;
  return h;
}

export function applyHomography(H, x, y) {
  const w = H[6] * x + H[7] * y + H[8];
  return [(H[0] * x + H[1] * y + H[2]) / w, (H[3] * x + H[4] * y + H[5]) / w];
}

// ─── Auto corner detection (Sobel-based edge heuristic) ──────────────────────
export function detectDocumentCorners(imageData, w, h) {
  const d = imageData.data;

  // Convert to grayscale
  const gray = new Float32Array(w * h);
  for (let i = 0; i < w * h; i++) {
    gray[i] = 0.299 * d[i * 4] + 0.587 * d[i * 4 + 1] + 0.114 * d[i * 4 + 2];
  }

  // Sobel edge magnitude
  const edge = new Float32Array(w * h);
  let maxEdge = 0;
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const gx =
        -gray[(y - 1) * w + (x - 1)] + gray[(y - 1) * w + (x + 1)]
        - 2 * gray[y * w + (x - 1)] + 2 * gray[y * w + (x + 1)]
        - gray[(y + 1) * w + (x - 1)] + gray[(y + 1) * w + (x + 1)];
      const gy =
        -gray[(y - 1) * w + (x - 1)] - 2 * gray[(y - 1) * w + x] - gray[(y - 1) * w + (x + 1)]
        + gray[(y + 1) * w + (x - 1)] + 2 * gray[(y + 1) * w + x] + gray[(y + 1) * w + (x + 1)];
      const mag = Math.sqrt(gx * gx + gy * gy);
      edge[y * w + x] = mag;
      if (mag > maxEdge) maxEdge = mag;
    }
  }

  const threshold = maxEdge * 0.15;

  // Find best corner in each quadrant
  const quadrants = [
    { xRange: [0, w / 2], yRange: [0, h / 2], bestX: w * 0.08, bestY: h * 0.08, score: 0 },
    { xRange: [w / 2, w], yRange: [0, h / 2], bestX: w * 0.92, bestY: h * 0.08, score: 0 },
    { xRange: [w / 2, w], yRange: [h / 2, h], bestX: w * 0.92, bestY: h * 0.92, score: 0 },
    { xRange: [0, w / 2], yRange: [h / 2, h], bestX: w * 0.08, bestY: h * 0.92, score: 0 },
  ];

  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const mag = edge[y * w + x];
      if (mag < threshold) continue;
      for (const q of quadrants) {
        if (x >= q.xRange[0] && x < q.xRange[1] && y >= q.yRange[0] && y < q.yRange[1]) {
          // Weight towards corners
          const cx = (q.xRange[0] + q.xRange[1]) / 2;
          const cy = (q.yRange[0] + q.yRange[1]) / 2;
          const cornerBias = Math.hypot(x - cx, y - cy) / Math.hypot(w / 2, h / 2);
          const score = mag * (0.5 + cornerBias);
          if (score > q.score) {
            q.score = score;
            q.bestX = x;
            q.bestY = y;
          }
        }
      }
    }
  }

  const margin = Math.min(w, h) * 0.04;
  return quadrants.map((q, i) => {
    // Clamp to image bounds with small inset
    const x = Math.max(margin, Math.min(w - margin, q.bestX));
    const y = Math.max(margin, Math.min(h - margin, q.bestY));
    return [x, y];
  });
}

// ─── Scan filters ─────────────────────────────────────────────────────────────
export function applyFilter(ctx, w, h, mode) {
  const imgData = ctx.getImageData(0, 0, w, h);
  const d = imgData.data;

  if (mode === "grayscale") {
    for (let i = 0; i < d.length; i += 4) {
      const g = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
      // Slight contrast boost
      const v = Math.min(255, Math.max(0, (g - 128) * 1.15 + 140));
      d[i] = d[i + 1] = d[i + 2] = v;
    }
  } else if (mode === "bw") {
    // Adaptive local threshold approximation
    const gray = new Float32Array(w * h);
    for (let i = 0; i < w * h; i++) {
      gray[i] = 0.299 * d[i * 4] + 0.587 * d[i * 4 + 1] + 0.114 * d[i * 4 + 2];
    }
    const blockSize = Math.max(16, Math.floor(Math.min(w, h) / 20));
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const y0 = Math.max(0, y - blockSize), y1 = Math.min(h - 1, y + blockSize);
        const x0 = Math.max(0, x - blockSize), x1 = Math.min(w - 1, x + blockSize);
        let sum = 0, count = 0;
        for (let by = y0; by <= y1; by += 4) {
          for (let bx = x0; bx <= x1; bx += 4) {
            sum += gray[by * w + bx]; count++;
          }
        }
        const localMean = sum / count;
        const v = gray[y * w + x] > localMean - 8 ? 255 : 0;
        const idx = (y * w + x) * 4;
        d[idx] = d[idx + 1] = d[idx + 2] = v;
      }
    }
  } else if (mode === "high_contrast") {
    for (let i = 0; i < d.length; i += 4) {
      const g = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
      const v = Math.min(255, Math.max(0, (g - 100) * 1.8 + 100));
      d[i] = d[i + 1] = d[i + 2] = v;
    }
  } else if (mode === "color") {
    for (let i = 0; i < d.length; i += 4) {
      d[i]     = Math.min(255, Math.max(0, (d[i]     - 128) * 1.25 + 145));
      d[i + 1] = Math.min(255, Math.max(0, (d[i + 1] - 128) * 1.25 + 145));
      d[i + 2] = Math.min(255, Math.max(0, (d[i + 2] - 128) * 1.25 + 145));
    }
  } else if (mode === "shadow_removal") {
    // Background normalization — helps with shadowed invoices
    const step = 16;
    const cols = Math.ceil(w / step), rows = Math.ceil(h / step);
    const bg = new Float32Array(w * h);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const gray = 0.299 * d[(y * w + x) * 4] + 0.587 * d[(y * w + x) * 4 + 1] + 0.114 * d[(y * w + x) * 4 + 2];
        bg[y * w + x] = gray;
      }
    }
    // Simple bilateral-like smoothing
    for (let i = 0; i < d.length; i += 4) {
      const g = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
      const bgVal = Math.max(150, bg[i / 4]);
      const normalized = Math.min(255, (g / bgVal) * 220);
      d[i] = d[i + 1] = d[i + 2] = normalized;
    }
  }

  ctx.putImageData(imgData, 0, 0);
}

// ─── Perspective warp ─────────────────────────────────────────────────────────
export function perspectiveWarp(srcCanvas, corners) {
  const [tl, tr, br, bl] = corners;
  const widthTop  = Math.hypot(tr[0] - tl[0], tr[1] - tl[1]);
  const widthBot  = Math.hypot(br[0] - bl[0], br[1] - bl[1]);
  const heightL   = Math.hypot(bl[0] - tl[0], bl[1] - tl[1]);
  const heightR   = Math.hypot(br[0] - tr[0], br[1] - tr[1]);
  const outW = Math.round(Math.max(widthTop, widthBot));
  const outH = Math.round(Math.max(heightL,  heightR));

  const dst = [[0, 0], [outW, 0], [outW, outH], [0, outH]];
  const H_inv = computeHomography(dst, corners);

  const srcCtx = srcCanvas.getContext("2d");
  const srcData = srcCtx.getImageData(0, 0, srcCanvas.width, srcCanvas.height);
  const sw = srcCanvas.width, sh = srcCanvas.height;

  const outCanvas = document.createElement("canvas");
  outCanvas.width = outW;
  outCanvas.height = outH;
  const outCtx = outCanvas.getContext("2d");
  const outData = outCtx.createImageData(outW, outH);
  const od = outData.data, sd = srcData.data;

  for (let y = 0; y < outH; y++) {
    for (let x = 0; x < outW; x++) {
      const [sx, sy] = applyHomography(H_inv, x, y);
      // Bilinear interpolation
      const x0 = Math.floor(sx), y0 = Math.floor(sy);
      const x1 = x0 + 1, y1 = y0 + 1;
      if (x0 < 0 || x1 > sw - 1 || y0 < 0 || y1 > sh - 1) {
        // Use nearest pixel if at boundary
        const cx = Math.max(0, Math.min(sw - 1, Math.round(sx)));
        const cy = Math.max(0, Math.min(sh - 1, Math.round(sy)));
        if (cx < 0 || cx >= sw || cy < 0 || cy >= sh) continue;
        const si = (cy * sw + cx) * 4;
        const di = (y * outW + x) * 4;
        od[di] = sd[si]; od[di+1] = sd[si+1]; od[di+2] = sd[si+2]; od[di+3] = 255;
        continue;
      }
      const fx = sx - x0, fy = sy - y0;
      const di = (y * outW + x) * 4;
      for (let c = 0; c < 3; c++) {
        const v00 = sd[(y0 * sw + x0) * 4 + c];
        const v10 = sd[(y0 * sw + x1) * 4 + c];
        const v01 = sd[(y1 * sw + x0) * 4 + c];
        const v11 = sd[(y1 * sw + x1) * 4 + c];
        od[di + c] = v00 * (1 - fx) * (1 - fy) + v10 * fx * (1 - fy) + v01 * (1 - fx) * fy + v11 * fx * fy;
      }
      od[di + 3] = 255;
    }
  }
  outCtx.putImageData(outData, 0, 0);
  return outCanvas;
}

// ─── Blob / File helpers ──────────────────────────────────────────────────────
export function canvasToBlob(canvas, quality = 0.92) {
  return new Promise(resolve => canvas.toBlob(resolve, "image/jpeg", quality));
}

export function dataUrlToBlob(dataUrl) {
  const [header, data] = dataUrl.split(",");
  const mime = header.match(/:(.*?);/)[1];
  const binary = atob(data);
  const arr = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) arr[i] = binary.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

export const SCAN_MODES = [
  { id: "grayscale",      label: "Escàner",        icon: "📄", desc: "Ideal per la majoria de factures" },
  { id: "bw",             label: "B/N net",         icon: "⬛", desc: "Text fosc sobre fons blanc" },
  { id: "high_contrast",  label: "Alt contrast",    icon: "🔲", desc: "Factures poc nítides" },
  { id: "color",          label: "Color",           icon: "🎨", desc: "Preserva colors originals" },
  { id: "shadow_removal", label: "Sense ombres",    icon: "☀️", desc: "Factures amb ombres o brillos" },
];